import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import './SuperAdminActivityLogsView.css';
import { schoolMatches } from '../utils/schoolScope';

function SuperAdminActivityLogsView() {
  const [logs, setLogs] = useState([]);
  const [students, setStudents] = useState([]);
  const [users, setUsers] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterSchool, setFilterSchool] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterDate, setFilterDate] = useState('all');

  const loadActivityLogs = useCallback(async () => {
    try {
      setLoading(true);
      // Load all logs then filter in memory so schoolId matches case-insensitively
      const snapshot = await getDocs(collection(db, 'studentActivityLogs'));
      const logsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Sort by timestamp descending
      logsData.sort((a, b) => {
        const timeA = a.timestamp?.toDate?.() || new Date(a.timestamp);
        const timeB = b.timestamp?.toDate?.() || new Date(b.timestamp);
        return timeB - timeA;
      });

      // Apply filters (case-insensitive school match)
      let filteredLogs = logsData;
      if (filterSchool !== 'all') {
        filteredLogs = filteredLogs.filter(log => schoolMatches(log.schoolId, filterSchool));
      }
      if (filterType !== 'all') {
        filteredLogs = filteredLogs.filter(log => log.scanType === filterType);
      }

      if (filterDate !== 'all') {
        const now = new Date();
        const cutoff = new Date();
        
        if (filterDate === 'today') {
          cutoff.setHours(0, 0, 0, 0);
        } else if (filterDate === 'week') {
          cutoff.setDate(now.getDate() - 7);
        } else if (filterDate === 'month') {
          cutoff.setMonth(now.getMonth() - 1);
        }
        
        filteredLogs = logsData.filter(log => {
          const logTime = log.timestamp?.toDate?.() || new Date(log.timestamp);
          return logTime >= cutoff;
        });
      }

      setLogs(filteredLogs);
    } catch (error) {
      console.error('Error loading activity logs:', error);
    } finally {
      setLoading(false);
    }
  }, [filterSchool, filterType, filterDate]);

  const loadStudents = useCallback(async () => {
    try {
      const snapshot = await getDocs(collection(db, 'students'));
      const studentsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setStudents(studentsData);
    } catch (error) {
      console.error('Error loading students:', error);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const snapshot = await getDocs(collection(db, 'users'));
      const usersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersData);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  }, []);

  const loadSchools = useCallback(async () => {
    try {
      const snapshot = await getDocs(collection(db, 'schools'));
      setSchools(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error('Error loading schools:', error);
    }
  }, []);

  useEffect(() => {
    loadActivityLogs();
    loadStudents();
    loadUsers();
    loadSchools();
  }, [loadActivityLogs, loadStudents, loadUsers, loadSchools]);

  const getStudentName = (studentId) => {
    const student = students.find(s => s.id === studentId);
    return student ? `${student.firstName} ${student.lastName}` : 'Unknown Student';
  };

  const getSchoolName = (schoolId) => {
    const school = schools.find(s => schoolMatches(s.id, schoolId));
    return school?.name || schoolId || 'N/A';
  };

  const getScannerName = (scannerId) => {
    const user = users.find(u => u.id === scannerId);
    return user ? user.username : 'Unknown Scanner';
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  if (loading) {
    return <div className="loading-container">Loading activity logs...</div>;
  }

  return (
    <div className="super-admin-activity-logs-view">
      <div className="view-header">
        <h2>Activity Logs</h2>
      </div>

      <div className="filters-bar">
        <div className="filter-group">
          <label>School:</label>
          <select value={filterSchool} onChange={(e) => setFilterSchool(e.target.value)}>
            <option value="all">All Schools</option>
            {schools.map(school => (
              <option key={school.id} value={school.id}>{school.name || school.id}</option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label>Type:</label>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="all">All Types</option>
            <option value="check-in">Check In</option>
            <option value="check-out">Check Out</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Date:</label>
          <select value={filterDate} onChange={(e) => setFilterDate(e.target.value)}>
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
          </select>
        </div>
      </div>

      <div className="logs-container">
        {logs.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <h3>No activity logs found</h3>
            <p>Activity logs will appear here when students are scanned</p>
          </div>
        ) : (
          <div className="logs-list">
            {logs.map(log => (
              <div key={log.id} className="log-entry">
                <div className="log-icon">
                  {log.scanType === 'check-in' ? '🟢' : '🔴'}
                </div>
                <div className="log-details">
                  <div className="log-header">
                    <span className="log-type">{log.scanType || 'Scan'}</span>
                    <span className="log-time">{formatTimestamp(log.timestamp)}</span>
                  </div>
                  <div className="log-info">
                    <p><strong>School:</strong> {getSchoolName(log.schoolId)}</p>
                    <p><strong>Student:</strong> {log.studentId ? getStudentName(log.studentId) : 'N/A'}</p>
                    <p><strong>Scanner:</strong> {log.scannerId ? getScannerName(log.scannerId) : 'N/A'}</p>
                    <p><strong>Location:</strong> {log.locationName || 'N/A'}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default SuperAdminActivityLogsView;
